import { useLazyQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import {
    DamagedParcelListingResponse,
    DamagedParcelListingVariables,
    GET_DAMAGED_PARCEL_LISTING_FOR_CENTRAL,
    GET_DAMAGED_PARCEL_LISTING_FOR_WMS,
    GetDamagedParcelListingForCentralResponse,
    GetDamagedParcelListingForWMSResponse,
} from '../graphql/queries/damagedParcelListingQueries';
import {
    DamagedLabelLookupInput,
    DamagedLabelLookupScope,
    toDamagedParcelFilters,
} from '../types/damagedLabelLookupTypes';

function pickListing(
    scope: DamagedLabelLookupScope,
    data?: GetDamagedParcelListingForCentralResponse | GetDamagedParcelListingForWMSResponse
): DamagedParcelListingResponse | undefined {
    if (!data) {
        return undefined;
    }
    return scope === 'central'
        ? (data as GetDamagedParcelListingForCentralResponse).getDamagedParcelListingForCentral
        : (data as GetDamagedParcelListingForWMSResponse).getDamagedParcelListingForWMS;
}

export function useDamagedLabelLookup(scope: DamagedLabelLookupScope) {
    const [resultsCleared, setResultsCleared] = useState(true);

    const queryDoc =
        scope === 'central'
            ? GET_DAMAGED_PARCEL_LISTING_FOR_CENTRAL
            : GET_DAMAGED_PARCEL_LISTING_FOR_WMS;

    const [searchQuery, { data, loading, error, refetch }] = useLazyQuery<
        GetDamagedParcelListingForCentralResponse | GetDamagedParcelListingForWMSResponse,
        DamagedParcelListingVariables
    >(queryDoc, {
        fetchPolicy: 'network-only',
    });

    const listing = useMemo(
        () => (resultsCleared ? undefined : pickListing(scope, data)),
        [resultsCleared, scope, data]
    );

    const parcelData = listing?.content ?? [];
    const totalElements = listing?.totalElements ?? 0;
    const accessiblePages = listing?.accessiblePages ?? undefined;
    const hasSearched = !resultsCleared;

    const buildVariables = useCallback(
        (criteria: DamagedLabelLookupInput, page: number, pageSize: number): DamagedParcelListingVariables => ({
            filters: toDamagedParcelFilters(criteria),
            pageRequest: { page, size: pageSize },
        }),
        []
    );

    const search = useCallback(
        async (criteria: DamagedLabelLookupInput, page: number, pageSize: number) => {
            setResultsCleared(false);
            const result = await searchQuery({
                variables: buildVariables(criteria, page, pageSize),
                errorPolicy: 'all',
            });
            const listingData = pickListing(scope, result.data);
            if (result.error && !listingData) {
                setResultsCleared(true);
                return { ok: false as const, error: result.error };
            }
            return { ok: true as const };
        },
        [buildVariables, scope, searchQuery]
    );

    const resetResults = useCallback(() => {
        setResultsCleared(true);
    }, []);

    const refetchSearch = useCallback(
        async (criteria: DamagedLabelLookupInput, page: number, pageSize: number) => {
            if (refetch) {
                await refetch(buildVariables(criteria, page, pageSize));
            }
        },
        [buildVariables, refetch]
    );

    return {
        parcelData,
        loading,
        error,
        totalElements,
        accessiblePages,
        hasSearched,
        search,
        refetchSearch,
        resetResults,
    };
}
